// src/components/Header.js
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import logoWhite from '../assets/logo-white.png';
import LanguageSelector from './LanguageSelector';
import { useTranslation } from 'react-i18next';

const Header = () => {
  const { t } = useTranslation();

  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const navItems = [
    { key: 'nav_home', href: '#home' },
    { key: 'nav_plans', href: '#planes' },
    { key: 'nav_benefits', href: '#beneficios' },
    { key: 'nav_contact', href: '#contacto' },
  ];

  return (
    <>
      <header
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
          scrolled
            ? 'bg-slate-950/90 backdrop-blur-xl shadow-lg'
            : 'bg-transparent'
        }`}
      >
        <div className="container mx-auto px-6">
          <div className="flex items-center justify-between h-20 md:h-24">
            {/* Logo */}
            <a href="#home" className="flex items-center">
              <img
                src={logoWhite}
                alt="TekNet"
                className="h-12 md:h-14 w-auto drop-shadow-lg"
              />
            </a>

            {/* Desktop nav */}
            <nav className="hidden md:flex items-center space-x-10">
              {navItems.map((item) => (
                <a
                  key={item.key}
                  href={item.href}
                  className="text-lg font-medium text-white hover:text-orange-400 transition relative after:absolute after:bottom-0 after:left-0 after:w-0 after:h-0.5 after:bg-orange-400 after:transition-all hover:after:w-full"
                >
                  {t(item.key)}
                </a>
              ))}

              {/* CTA */}
              <a
                href="#contacto"
                className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-8 py-3 rounded-full font-semibold shadow-lg hover:shadow-orange-500/40 hover:scale-105 transition-all duration-300"
              >
                {t('nav_contact')}
              </a>

              <LanguageSelector />
            </nav>

            {/* Mobile button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden text-white"
            >
              {mobileMenuOpen ? <X size={32} /> : <Menu size={32} />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 z-40 bg-slate-950/95 backdrop-blur-2xl pt-32 px-6"
          onClick={() => setMobileMenuOpen(false)}
        >
          <motion.nav
            initial={{ y: 40 }}
            animate={{ y: 0 }}
            className="flex flex-col items-center space-y-10"
            onClick={(e) => e.stopPropagation()}
          >
            {navItems.map((item, i) => (
              <motion.a
                key={item.key}
                href={item.href}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                className="text-4xl font-light text-white hover:text-orange-400"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t(item.key)}
              </motion.a>
            ))}

            {/* Mobile CTA */}
            <motion.a
              href="#contacto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="mt-10 bg-gradient-to-r from-orange-500 to-orange-600 text-white px-16 py-6 rounded-full text-2xl font-bold shadow-2xl hover:scale-105 transition"
              onClick={() => setMobileMenuOpen(false)}
            >
              {t('nav_contact')}
            </motion.a>

            <div className="mt-10">
              <LanguageSelector />
            </div>
          </motion.nav>
        </motion.div>
      )}
    </>
  );
};

export default Header;
